import { lazy, Suspense } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router-dom'

import * as Sentry from '@sentry/react'

import { redirectIfAuthenticated } from './auth'
import { Account, loader as accountLoader } from './routes/account'
import { action as accountDestroy } from './routes/account-destroy'
import { AccountEdit, loader as accountEditLoader } from './routes/account-edit'
import { AccountNew, action as accountNewAction } from './routes/account-new'
import { Dashboard, loader as dashboardLoader } from './routes/dashboard'
import { Login } from './routes/login'
import { loader as rootLoader, Root } from './routes/root'
import { Signup } from './routes/signup'
import { Site, loader as siteLoader } from './routes/site'
import { Website, loader as websiteLoader } from './routes/website'
import { action as websiteDestroy } from './routes/website-destroy'
import { WebsiteEdit, loader as websiteEditLoader } from './routes/website-edit'
import { action as websiteNewAction, WebsiteNew } from './routes/website-new'
import { WebsiteWizard } from './routes/website-wizard'

// Resolved lazily and only in dev builds, so production drops the module and its
// mock data entirely — a static import ships the fake sites even with the route
// itself switched off.
const DevSandbox = import.meta.env.DEV
	? lazy(() => import('./sandbox').then((m) => ({ default: m.Sandbox })))
	: () => null

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter)

export const router = sentryCreateBrowserRouter([
	{
		path: '/login',
		loader: redirectIfAuthenticated,
		element: <Login />,
	},
	{
		path: '/signup',
		loader: redirectIfAuthenticated,
		element: <Signup />,
	},
	// Review sandbox for the onboarding redesign — the real components with mock
	// data, no auth and no API. Dev builds only: it is review scaffolding, and a
	// public unauthenticated page full of fake sites has no business in
	// production. Outside the `root` branch so it opens without signing in.
	...(import.meta.env.DEV
		? [
				{
					path: '/sandbox',
					element: (
						<Suspense fallback={null}>
							<DevSandbox />
						</Suspense>
					),
				},
			]
		: []),
	{
		id: 'root',
		path: '/',
		element: <Root />,
		loader: rootLoader,
		children: [
			{
				index: true,
				element: <Dashboard />,
				loader: dashboardLoader,
			},
			{
				path: 'website/new',
				element: <WebsiteWizard />,
			},
			{
				path: 'site/:websiteID',
				element: <Site />,
				loader: siteLoader,
			},
			{
				path: 'account/new',
				element: <AccountNew />,
				action: accountNewAction,
			},
			{
				path: 'account/:accountID',
				children: [
					{
						index: true,
						element: <Account />,
						loader: accountLoader,
					},
					{
						path: 'website/new',
						element: <WebsiteNew />,
						action: websiteNewAction,
					},
					{
						path: 'website/:websiteID',
						element: <Website />,
						loader: websiteLoader,
					},
					{
						path: 'website/:websiteID/edit',
						element: <WebsiteEdit />,
						loader: websiteEditLoader,
					},
					{
						path: 'website/:websiteID/destroy',
						action: websiteDestroy,
					},
				],
			},
			{
				path: 'account/:accountID/edit',
				element: <AccountEdit />,
				loader: accountEditLoader,
			},
			{
				path: 'account/:accountID/destroy',
				action: accountDestroy,
			},
		],
	},
] satisfies RouteObject[])
